import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const StudentManagement = () => {
    const [allStudents, setAllStudents] = useState([]);
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [filterOptions, setFilterOptions] = useState({
        batches: [],
        semesters: [],
        programs: [],
        departments: [],
        statuses: [],
        admittedYears: []
    });
    const [filters, setFilters] = useState({
        batch: '',
        semester: '',
        program: '',
        department: '',
        status: '',
        admittedYear: '',
        search: ''
    });

    const fetchFilterOptions = useCallback(async () => {
        try {
            const [batchRes, semesterRes, programRes, deptRes] = await Promise.all([
                axios.get('/api/batches', { timeout: 10000 }),
                axios.get('/api/semesters', { timeout: 10000 }),
                axios.get('/api/programs', { timeout: 10000 }),
                axios.get('/api/departments', { timeout: 10000 })
            ]);

            setFilterOptions((prev) => ({
                ...prev,
                batches: (Array.isArray(batchRes.data) ? batchRes.data : [])
                    .map((b) => b.batch_name)
                    .filter(Boolean)
                    .sort(),
                semesters: (Array.isArray(semesterRes.data) ? semesterRes.data : [])
                    .map((s) => String(s.sem_no ?? s.sem_name ?? ''))
                    .filter(Boolean)
                    .sort((a, b) => Number(a) - Number(b)),
                programs: (Array.isArray(programRes.data) ? programRes.data : [])
                    .map((p) => p.pgm_name)
                    .filter(Boolean)
                    .sort(),
                departments: (Array.isArray(deptRes.data) ? deptRes.data : [])
                    .map((d) => d.dept_name)
                    .filter(Boolean)
                    .sort()
            }));
        } catch (err) {
            console.error('❌ Failed to load filter options:', err);
        }
    }, []);

    const fetchStudents = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/admin/students', {
                timeout: 10000
            });
            const list = Array.isArray(response.data) ? response.data : [];
            setAllStudents(list);
        } catch (err) {
            console.error('❌ Failed to load students:', err);
            setAllStudents([]);
            setStudents([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const getInferredSemester = (student) => {
            const directSemester = String(student.semester || student.current_semester || student.sem_no || '').trim();
            if (directSemester) return directSemester;

            const admittedYear = Number(student.admitted_year || 0);
            if (!admittedYear) return '';

            const now = new Date();
            const currentYear = now.getFullYear();
            const month = now.getMonth() + 1;
            const yearOffset = Math.max(0, currentYear - admittedYear);
            const semFromYear = (yearOffset * 2) + (month >= 6 ? 2 : 1);
            return String(Math.min(8, Math.max(1, semFromYear)));
        };

        const normalizedBatch = (filters.batch || '').trim().toLowerCase();
        const normalizedProgram = (filters.program || '').trim().toLowerCase();
        const normalizedDepartment = (filters.department || '').trim().toLowerCase();
        const normalizedSemester = (filters.semester || '').trim().toLowerCase();
        const normalizedStatus = (filters.status || '').trim().toLowerCase();
        const normalizedAdmittedYear = (filters.admittedYear || '').trim().toLowerCase();
        const normalizedSearch = (filters.search || '').trim().toLowerCase();
        const hasSemesterData = allStudents.some((student) =>
            Boolean(getInferredSemester(student))
        );

        const filtered = allStudents.filter((student) => {
            const studentBatch = String(student.batch || '').trim().toLowerCase();
            const studentProgram = String(student.program || '').trim().toLowerCase();
            const studentDepartment = String(student.department || '').trim().toLowerCase();
            const studentSemester = getInferredSemester(student).toLowerCase();
            const studentStatus = String(student.status || '').trim().toLowerCase();
            const studentAdmittedYear = String(student.admitted_year || '').trim().toLowerCase();
            const searchable = [
                student.name,
                student.reg_no,
                student.std_id,
                student.roll_no,
                student.email,
                student.phone,
                student.batch,
                student.program,
                student.department,
                student.status
            ].map((v) => String(v || '').toLowerCase()).join(' ');

            const batchMatch = !normalizedBatch || studentBatch === normalizedBatch;
            const programMatch = !normalizedProgram || studentProgram === normalizedProgram;
            const departmentMatch = !normalizedDepartment || studentDepartment === normalizedDepartment;
            const semesterMatch = !normalizedSemester || !hasSemesterData || studentSemester === normalizedSemester;
            const statusMatch = !normalizedStatus || studentStatus === normalizedStatus;
            const admittedYearMatch = !normalizedAdmittedYear || studentAdmittedYear === normalizedAdmittedYear;
            const searchMatch = !normalizedSearch || searchable.includes(normalizedSearch);

            return (
                batchMatch &&
                programMatch &&
                departmentMatch &&
                semesterMatch &&
                statusMatch &&
                admittedYearMatch &&
                searchMatch
            );
        });

        setStudents(filtered);
    }, [allStudents, filters]);

    useEffect(() => {
        fetchStudents();
        fetchFilterOptions();
    }, [fetchStudents, fetchFilterOptions]);

    useEffect(() => {
        const statuses = [...new Set(allStudents.map((s) => s.status).filter(Boolean))].sort();
        const admittedYears = [...new Set(allStudents.map((s) => s.admitted_year).filter(Boolean))]
            .map((y) => String(y))
            .sort((a, b) => Number(a) - Number(b));

        setFilterOptions((prev) => ({
            ...prev,
            statuses,
            admittedYears
        }));
    }, [allStudents]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const resetFilters = () => {
        setFilters({
            batch: '',
            semester: '',
            program: '',
            department: '',
            status: '',
            admittedYear: '',
            search: ''
        });
    };

    const handleViewDetails = (student) => {
        setSelectedStudent(student);
    };

    const handleRefresh = async () => {
        await Promise.all([fetchStudents(), fetchFilterOptions()]);
    };

    const totalStudents = students.length;
    const activeStudents = students.filter(s => (s.status === 'Active' || !s.status)).length;
    const batchOptions = filterOptions.batches.length
        ? filterOptions.batches
        : [...new Set(allStudents.map(s => s.batch).filter(Boolean))].sort();
    const programOptions = filterOptions.programs.length
        ? filterOptions.programs
        : [...new Set(allStudents.map(s => s.program).filter(Boolean))].sort();
    const semesterOptions = filterOptions.semesters.length
        ? filterOptions.semesters
        : [...new Set(allStudents.map(s => s.semester || s.current_semester || s.sem_no).filter(Boolean))]
            .map((v) => String(v))
            .sort((a, b) => Number(a) - Number(b));
    const departmentOptions = filterOptions.departments.length
        ? filterOptions.departments
        : [...new Set(allStudents.map(s => s.department).filter(Boolean))].sort();
    const statusOptions = filterOptions.statuses;
    const admittedYearOptions = filterOptions.admittedYears;

    if (loading) return <div style={{padding: '50px', textAlign: 'center', fontSize: '18px'}}>📊 Loading Students...</div>;

    return (
        <div style={containerStyle}>
            {/* Header */}
            <div style={headerStyle}>
                <div style={headerLeftStyle}>
                    <span style={iconStyle}>👨‍🎓</span>
                    <div>
                        <h1 style={pageTitleStyle}>Student Management</h1>
                        <p style={pageSubtitleStyle}>{totalStudents} students | {activeStudents} active</p>
                    </div>
                </div>
                <button onClick={handleRefresh} style={refreshBtnStyle}>🔄 Refresh</button>
            </div>

            {/* Stats Cards */}
            <div style={statsRowStyle}>
                <div style={statCardStyle('#3b82f6')}>
                    <span style={statIconStyle}>👨‍🎓</span>
                    <div>
                        <div style={statLabelStyle}>Total Students</div>
                        <div style={statValueStyle}>{totalStudents}</div>
                    </div>
                </div>
                <div style={statCardStyle('#10b981')}>
                    <span style={statIconStyle}>✅</span>
                    <div>
                        <div style={statLabelStyle}>Active Students</div>
                        <div style={statValueStyle}>{activeStudents}</div>
                    </div>
                </div>
                <div style={statCardStyle('#f59e0b')}>
                    <span style={statIconStyle}>🎓</span>
                    <div>
                        <div style={statLabelStyle}>Programs</div>
                        <div style={statValueStyle}>{new Set(students.map(s => s.program)).size}</div>
                    </div>
                </div>
            </div>

            {/* Filter Bar */}
            <div style={filterCardStyle}>
                <div style={tableHeaderStyle}>
                    <h3 style={cardTitleStyle}>Filters</h3>
                </div>
                <div style={filterBarStyle}>
                    <div style={filterGroupStyle}>
                        <label style={labelStyle}>Search</label>
                        <input
                            name="search"
                            value={filters.search}
                            onChange={handleFilterChange}
                            placeholder="Name, reg no, email, phone..."
                            style={selectStyle}
                        />
                    </div>
                    <div style={filterGroupStyle}>
                        <label style={labelStyle}>Batch</label>
                        <select name="batch" value={filters.batch} onChange={handleFilterChange} style={selectStyle}>
                            <option value="">All Batches</option>
                            {batchOptions.map((batch) => (
                                <option key={batch} value={batch}>{batch}</option>
                            ))}
                        </select>
                    </div>
                    <div style={filterGroupStyle}>
                        <label style={labelStyle}>Semester</label>
                        <select
                            name="semester"
                            value={filters.semester}
                            onChange={handleFilterChange}
                            style={selectStyle}
                        >
                            <option value="">All Semesters</option>
                            {semesterOptions.map((s) => (
                                <option key={String(s)} value={String(s)}>{`Semester ${s}`}</option>
                            ))}
                        </select>
                    </div>
                    <div style={filterGroupStyle}>
                        <label style={labelStyle}>Program</label>
                        <select name="program" value={filters.program} onChange={handleFilterChange} style={selectStyle}>
                            <option value="">All Programs</option>
                            {programOptions.map((program) => (
                                <option key={program} value={program}>{program}</option>
                            ))}
                        </select>
                    </div>
                    <div style={filterGroupStyle}>
                        <label style={labelStyle}>Department</label>
                        <select name="department" value={filters.department} onChange={handleFilterChange} style={selectStyle}>
                            <option value="">All Departments</option>
                            {departmentOptions.map((department) => (
                                <option key={department} value={department}>{department}</option>
                            ))}
                        </select>
                    </div>
                    <div style={filterGroupStyle}>
                        <label style={labelStyle}>Status</label>
                        <select name="status" value={filters.status} onChange={handleFilterChange} style={selectStyle}>
                            <option value="">All Statuses</option>
                            {statusOptions.map((status) => (
                                <option key={status} value={status}>{status}</option>
                            ))}
                        </select>
                    </div>
                    <div style={filterGroupStyle}>
                        <label style={labelStyle}>Admitted Year</label>
                        <select name="admittedYear" value={filters.admittedYear} onChange={handleFilterChange} style={selectStyle}>
                            <option value="">All Years</option>
                            {admittedYearOptions.map((year) => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{ ...filterGroupStyle, justifyContent: 'flex-end' }}>
                        <button onClick={resetFilters} style={refreshBtnStyle}>Clear Filters</button>
                    </div>
                </div>
            </div>

            {/* Table Card */}
            <div style={tableCardStyle}>
                <div style={tableHeaderStyle}>
                    <h3 style={cardTitleStyle}>Student List ({totalStudents})</h3>
                </div>
                <div style={{overflowX: 'auto'}}>
                    <table style={tableStyle}>
                        <thead style={theadStyle}>
                            <tr>
                                <th style={thStyle}>Reg No</th>
                                <th style={thStyle}>Name</th>
                                <th style={thStyle}>Program</th>
                                <th style={thStyle}>Batch</th>
                                <th style={thStyle}>Status</th>
                                <th style={thStyle}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {students.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={emptyCellStyle}>
                                        <div style={{textAlign: 'center', padding: '60px'}}>
                                            <span style={{fontSize: '48px', display: 'block', marginBottom: '16px'}}>👨‍🎓</span>
                                            <div style={{fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '8px'}}>No Students Found</div>
                                            <div style={{color: '#64748b'}}>Try adjusting your filters</div>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                students.map((student, index) => (
                                    <tr key={student.id || index} style={trStyle}>
                                        <td style={tdStyle}>
                                            <span style={courseCodeStyle}>{student.reg_no}</span>
                                        </td>
                                        <td style={tdStyle}>
                                            <span style={facultyStyle}>👤 {student.name}</span>
                                        </td>
                                        <td style={tdStyle}>{student.program}</td>
                                        <td style={tdStyle}>{student.batch}</td>
                                        <td style={tdNumberStyle}>
                                            <span style={badgeStyle(student.status === 'Active' || !student.status ? '#10b981' : '#ef4444')}>
                                                {student.status || 'Active'}
                                            </span>
                                        </td>
                                        <td style={tdNumberStyle}>
                                            <button onClick={() => handleViewDetails(student)} style={actionBtnStyle}>
                                                👁️ View
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Student Detail Modal */}
            {selectedStudent && (
                <div style={modalOverlayStyle}>
                    <div style={modalContentStyle}>
                        <div style={modalHeaderStyle}>
                            <h3 style={modalTitleStyle}>Student Profile</h3>
                            <button onClick={() => setSelectedStudent(null)} style={closeBtnStyle}>✕</button>
                        </div>
                        <div style={profileGridStyle}>
                            <div style={infoBlockStyle}>
                                <label style={labelStyle}>Full Name</label>
                                <p style={infoTextStyle}>{selectedStudent.name}</p>
                            </div>
                            <div style={infoBlockStyle}>
                                <label style={labelStyle}>Reg No</label>
                                <p style={infoTextStyle}>{selectedStudent.reg_no}</p>
                            </div>
                            <div style={infoBlockStyle}>
                                <label style={labelStyle}>Email</label>
                                <p style={infoTextStyle}>{selectedStudent.email}</p>
                            </div>
                            <div style={infoBlockStyle}>
                                <label style={labelStyle}>Phone</label>
                                <p style={infoTextStyle}>{selectedStudent.phone || 'N/A'}</p>
                            </div>
                            <div style={infoBlockStyle}>
                                <label style={labelStyle}>Program</label>
                                <p style={infoTextStyle}>{selectedStudent.program}</p>
                            </div>
                            <div style={infoBlockStyle}>
                                <label style={labelStyle}>Batch</label>
                                <p style={infoTextStyle}>{selectedStudent.batch}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- STYLES PRESERVED EXACTLY AS PROVIDED ---
const containerStyle = {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '32px',
    background: '#f8fafc',
    fontFamily: 'system-ui, -apple-system, sans-serif'
};

const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
    padding: '24px 0'
};

const headerLeftStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
};

const iconStyle = {
    fontSize: '28px'
};

const pageTitleStyle = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 4px 0'
};

const pageSubtitleStyle = {
    fontSize: '16px',
    color: '#64748b',
    margin: 0
};

const refreshBtnStyle = {
    padding: '8px 16px',
    background: '#f8fafc',
    color: '#1e3a8a',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '13px',
    cursor: 'pointer'
};

const statsRowStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '24px',
    marginBottom: '32px'
};

const statCardStyle = (color) => ({
    background: '#fff',
    padding: '24px',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    borderLeft: `4px solid ${color}`,
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
});

const statIconStyle = {
    fontSize: '24px'
};

const statLabelStyle = {
    fontSize: '14px',
    color: '#64748b',
    fontWeight: '500',
    marginBottom: '4px'
};

const statValueStyle = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b'
};

const filterCardStyle = {
    background: '#fff',
    borderRadius: '20px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    marginBottom: '32px'
};

const tableHeaderStyle = {
    padding: '28px 32px',
    borderBottom: '1px solid #f1f5f9'
};

const cardTitleStyle = {
    fontSize: '22px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0
};

const filterBarStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '24px',
    padding: '32px'
};

const filterGroupStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
};

const labelStyle = {
    fontSize: '13px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase'
};

const selectStyle = {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    background: '#fff',
    fontSize: '14px',
    outline: 'none'
};

const tableCardStyle = {
    background: '#fff',
    borderRadius: '20px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
    border: '1px solid #e2e8f0',
    overflow: 'hidden'
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
};

const theadStyle = {
    background: '#f8fafc'
};

const thStyle = {
    textAlign: 'left',
    padding: '20px 24px',
    color: '#64748b',
    fontSize: '13px',
    fontWeight: '600',
    textTransform: 'uppercase',
    borderBottom: '2px solid #f1f5f9'
};

const trStyle = {
    background: '#fff'
};

const tdStyle = {
    padding: '20px 24px',
    fontSize: '15px',
    color: '#1e293b',
    verticalAlign: 'middle',
    borderBottom: '1px solid #f8fafc'
};

const tdNumberStyle = {
    padding: '20px 24px',
    textAlign: 'center',
    verticalAlign: 'middle',
    borderBottom: '1px solid #f8fafc'
};

const courseCodeStyle = {
    fontWeight: '600',
    background: '#eff6ff',
    color: '#1e3a8a',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '14px'
};

const facultyStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px'
};

const badgeStyle = (color) => ({
    padding: '8px 16px',
    borderRadius: '20px',
    fontWeight: '600',
    fontSize: '14px',
    border: `2px solid ${color}`,
    backgroundColor: `${color}20`,
    color: color,
    display: 'inline-block',
    minWidth: '80px'
});

const actionBtnStyle = {
    padding: '8px 16px',
    background: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
};

const emptyCellStyle = {
    padding: 0,
    textAlign: 'left'
};

const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
};

const modalContentStyle = {
    background: '#fff',
    width: '90vw',
    maxWidth: '600px',
    maxHeight: '90vh',
    borderRadius: '20px',
    overflowY: 'auto',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
};

const modalHeaderStyle = {
    padding: '32px 32px 0 32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #f1f5f9',
    paddingBottom: '24px'
};

const modalTitleStyle = {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
};

const closeBtnStyle = {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#64748b',
    padding: '8px',
    borderRadius: '8px',
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const profileGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '24px',
    padding: '32px'
};

const infoBlockStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
};

const infoTextStyle = {
    margin: 0,
    fontWeight: '600',
    color: '#1e293b',
    fontSize: '16px'
};

export default StudentManagement;